import type { FrontProduct } from "lib/frontPageData";

type ExtensionProductCardProps = {
  product: FrontProduct;
};

export default function ExtensionProductCard({ product }: ExtensionProductCardProps) {
  const { tag, title, description, features, image, installUrl, tutorialUrl } = product;
  const tagClass = "text-[#0088cc]";
  const bulletDot = "before:bg-[#0088cc]";
  const primaryBtn = "bg-[#0088cc] text-white hover:opacity-90";

  const open = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="group relative z-0 flex h-full flex-col overflow-visible rounded-[20px] border border-[var(--dash-border)] bg-[var(--dash-surface)] shadow-[var(--dash-shadow)] transition-all duration-300 ease-out hover:z-10 hover:-translate-y-1 hover:border-[var(--theme-accent)] hover:bg-[var(--dash-surface-hover)] hover:shadow-xl">
      <div className="relative w-full shrink-0 overflow-visible rounded-t-[20px] bg-[var(--dash-surface)] px-5 pb-5 pt-5 md:px-7 md:pb-6 md:pt-7">
        <div className="relative h-[280px] w-full overflow-visible md:h-[320px]">
          <img
            src={image}
            alt={title}
            className="h-full w-full min-h-[240px] object-cover object-center md:min-h-[280px]"
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col overflow-visible rounded-b-[20px] px-6 pb-6 pt-5 md:px-7 md:pb-7 md:pt-6">
        <div className={`mb-2 text-[0.65rem] font-extrabold uppercase tracking-[0.2em] ${tagClass}`}>
          {tag}
        </div>
        <h3 className="mb-3 text-xl font-bold leading-tight text-[var(--dash-text-strong)] md:text-2xl">
          {title}
        </h3>
        <p className="mb-6 text-sm leading-relaxed text-[var(--dash-text)]">{description}</p>
        <ul className="mb-8 flex flex-1 list-none flex-col gap-3 p-0 text-[0.85rem] leading-snug">
          {features.map((f) => (
            <li
              key={`${f.bold}-${f.text}`}
              className={`flex items-start before:mr-3 before:mt-[0.35rem] before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full ${bulletDot}`}
            >
              <span className="text-[var(--dash-text)]">
                <strong className="font-bold text-[var(--dash-text-strong)]">{f.bold}</strong>
                {f.text}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-auto flex gap-3">
          <button
            type="button"
            onClick={() => open(installUrl)}
            className={`flex-1 rounded-[10px] py-3.5 text-xs font-bold uppercase tracking-wide transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${primaryBtn}`}
          >
            Install Free
          </button>
          <button
            type="button"
            onClick={() => open(tutorialUrl)}
            className="flex-1 rounded-[10px] bg-[var(--dash-elevated)] py-3.5 text-xs font-bold uppercase tracking-wide text-[var(--dash-text-strong)] transition-colors hover:bg-[var(--dash-surface-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
          >
            Watch Tutorial
          </button>
        </div>
      </div>
    </div>
  );
}
